// components/AccountDetailModal.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  User,
  ExternalLink,
  Loader,
  Briefcase,
  DollarSign,
  Users,
  AlertCircle,
  Package,
  Calendar,
  Wifi,
  PhoneCall,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const AccountDetailModal = ({ accountId, onClose, onNavigate }) => {
  const { isDarkMode } = useTheme();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    fetchAccountData();
  }, [accountId]);

  const fetchAccountData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAccount(accountId);
      setAccount(response.data);
      if (response.data.contacts) setContacts(response.data.contacts);
      if (response.data.opportunities)
        setOpportunities(response.data.opportunities);
      if (response.data.cases) setCases(response.data.cases);

      const projectsRes = await apiService.getProjectsByAccount(accountId);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error("Error fetching account:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";
  };

  const getAccountStatusColor = (status) => {
    const colors = {
      active: "text-green-400 bg-green-400/10 border-green-400/20",
      inactive: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
      dead: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
        <div
          className={`rounded-lg p-8 ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
        >
          <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
          <p className={`mt-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Loading account...
          </p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
        <div
          className={`rounded-lg p-8 max-w-md ${isDarkMode ? "bg-[#13161B]" : "bg-white"}`}
        >
          <p className={`mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Account not found
          </p>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg transition-colors ${
              isDarkMode
                ? "bg-gray-700 hover:bg-gray-600 text-white"
                : "bg-gray-100 hover:bg-gray-200 text-gray-900"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4">
      <div
        className={`rounded-lg border max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          isDarkMode
            ? "bg-[#13161B] border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center gap-4 flex-1">
            <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-lg">
              {getInitials(account.name)}
            </div>
            <div className="flex-1 min-w-0">
              <h2
                className={`text-xl font-bold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {account.name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                {account.type && (
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                    {account.type}
                  </span>
                )}
                {account.accountStatus && (
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium border ${getAccountStatusColor(account.accountStatus)}`}
                  >
                    {account.accountStatus.charAt(0).toUpperCase() +
                      account.accountStatus.slice(1)}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onNavigate(account.id)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 text-sm text-white"
            >
              <ExternalLink className="w-4 h-4" />
              Go to Account
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-200"
              }`}
            >
              <X
                className={`w-5 h-5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`px-6 border-b flex gap-6 ${
            isDarkMode
              ? "bg-[#0C0E12] border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          {[
            { id: "overview", label: "Overview" },
            { id: "contacts", label: `Contacts (${contacts.length})` },
            { id: "related", label: "Related" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-500 font-medium"
                  : `border-transparent ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-2 gap-4">
                {account.phone && (
                  <a
                    href={`tel:${account.phone}`}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Phone
                      </div>
                      <div
                        className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {account.phone}
                      </div>
                    </div>
                  </a>
                )}

                {account.email && (
                  <a
                    href={`mailto:${account.email}`}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Email
                      </div>
                      <div
                        className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {account.email}
                      </div>
                    </div>
                  </a>
                )}

                {account.website && (
                  <a
                    href={account.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      isDarkMode
                        ? "bg-gray-800/50 hover:bg-gray-800"
                        : "bg-gray-50 hover:bg-gray-100"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Website
                      </div>
                      <div
                        className={`text-sm truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {account.website}
                      </div>
                    </div>
                  </a>
                )}

                {(account.billingStreet || account.billingCity) && (
                  <div
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      isDarkMode ? "bg-gray-800/50" : "bg-gray-50"
                    }`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-orange-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Address
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {[
                          account.billingStreet,
                          account.billingCity,
                          account.billingState,
                          account.billingPostalCode,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Account Details */}
              <div
                className={`rounded-lg p-4 border ${
                  isDarkMode
                    ? "bg-gray-800/30 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <h3
                  className={`font-semibold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Account Details
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Industry", value: account.industry },
                    {
                      label: "Annual Revenue",
                      value: account.annualRevenue
                        ? `£${account.annualRevenue.toLocaleString()}`
                        : null,
                    },
                    { label: "Employees", value: account.numberOfEmployees },
                    { label: "Rating", value: account.rating },
                    {
                      label: "Owner",
                      value: account.owner
                        ? `${account.owner.firstName} ${account.owner.lastName}`
                        : null,
                    },
                    {
                      label: "Created",
                      value: account.createdAt
                        ? new Date(account.createdAt).toLocaleDateString(
                            "en-GB",
                          )
                        : null,
                    },
                  ].map(({ label, value }) =>
                    value ? (
                      <div key={label}>
                        <div
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {label}
                        </div>
                        <div
                          className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {value}
                        </div>
                      </div>
                    ) : null,
                  )}
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <Users className="w-5 h-5 text-blue-400 mb-2" />
                  <div
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {contacts.length}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Contacts
                  </div>
                </div>
                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-400 mb-2" />
                  <div
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {opportunities.length}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Opportunities
                  </div>
                </div>
                <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <Briefcase className="w-5 h-5 text-purple-400 mb-2" />
                  <div
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {projects.length}
                  </div>
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Projects
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "contacts" && (
            <div>
              {contacts.length === 0 ? (
                <div
                  className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <Users size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No contacts found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`p-4 rounded-lg transition-colors ${
                        isDarkMode
                          ? "bg-gray-800/50 hover:bg-gray-800"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                            {getInitials(
                              `${contact.firstName} ${contact.lastName}`,
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {contact.firstName} {contact.lastName}
                            </div>
                            {contact.title && (
                              <div
                                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                {contact.title}
                              </div>
                            )}
                            <div className="flex flex-wrap gap-3 mt-2">
                              {contact.email && (
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Mail className="w-3 h-3" />
                                  {contact.email}
                                </a>
                              )}
                              {contact.phone && (
                                <a
                                  href={`tel:${contact.phone}`}
                                  className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Phone className="w-3 h-3" />
                                  {contact.phone}
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "related" && (
            <div className="space-y-6">
              {/* Opportunities */}
              {opportunities.length > 0 && (
                <div>
                  <h3
                    className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <DollarSign size={18} />
                    Opportunities ({opportunities.length})
                  </h3>
                  <div className="space-y-2">
                    {opportunities.map((opp) => (
                      <div
                        key={opp.id}
                        className={`p-3 rounded-lg transition-colors ${
                          isDarkMode
                            ? "bg-gray-800/50 hover:bg-gray-800"
                            : "bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <div
                          className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {opp.name}
                        </div>
                        <div
                          className={`flex items-center gap-3 mt-1 text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          <span className="text-green-400">
                            £{opp.amount?.toLocaleString() || "0"}
                          </span>
                          <span>•</span>
                          <span className="capitalize">
                            {opp.salesStatus?.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Projects */}
              {projects.length > 0 && (
                <div>
                  <h3
                    className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Briefcase size={18} />
                    Projects ({projects.length})
                  </h3>
                  <div className="space-y-2">
                    {projects.map((project) => (
                      <div
                        key={project.id}
                        className={`p-3 rounded-lg transition-colors ${
                          isDarkMode
                            ? "bg-gray-800/50 hover:bg-gray-800"
                            : "bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <div
                          className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {project.projectName}
                        </div>
                        <div
                          className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {project.projectNumber} • {project.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cases */}
              {cases.length > 0 && (
                <div>
                  <h3
                    className={`text-lg font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <AlertCircle size={18} />
                    Cases ({cases.length})
                  </h3>
                  <div className="space-y-2">
                    {cases.map((caseItem) => (
                      <div
                        key={caseItem.id}
                        className={`p-3 rounded-lg transition-colors ${
                          isDarkMode
                            ? "bg-gray-800/50 hover:bg-gray-800"
                            : "bg-gray-50 hover:bg-gray-100"
                        }`}
                      >
                        <div
                          className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {caseItem.subject}
                        </div>
                        <div
                          className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {caseItem.caseNumber} • {caseItem.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {opportunities.length === 0 &&
                projects.length === 0 &&
                cases.length === 0 && (
                  <div
                    className={`text-center py-12 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <Package size={48} className="mx-auto mb-3 opacity-50" />
                    <p>No related records found</p>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetailModal;
